/**
 * v67 (band designs W1) — the host-side rules behind the pavé rows, the 0.15 mm
 * groove floor and the free-stone Size field. Pure, so `check-ring-designs-w1`
 * can pin them without a browser; the tabs call these and nothing else decides.
 */
import {
  DESIGN_GROOVE_MIN_WIDTH_MM,
  JUNCTION_SCALE_MAX,
  JUNCTION_SCALE_MIN,
} from "../../shared/api-contract";
import type { DiamondSetting, DiamondStyle, FreeStone, StoneShape } from "../../shared/api-contract";
import type { Caps } from "../editor/caps";

type Chip = { id: DiamondSetting; label: string };

/** The settings that hold no metal over the girdle, so the stone is the cut's
 *  BARE stone (no prong composite). Flush and Only since v39. */
const HELD_SETTINGS: Chip[] = [
  { id: "flush", label: "Flush" },
  { id: "only", label: "Only" },
];

/** v67: held by metal shared between neighbours — beads (pavé) or claws. */
export const JUNCTION_SETTINGS: Chip[] = [
  { id: "pave", label: "Pavé" },
  { id: "shared-prong", label: "Shared prong" },
];

export const isJunctionSetting = (s: DiamondSetting | undefined | null): s is "pave" | "shared-prong" =>
  s === "pave" || s === "shared-prong";

/** The Diamond Setting chips the connected viewer can build. An older viewer
 *  would store "pave" and build a plain bored seat, so it never sees them. */
export function heldSettingChips(caps: Pick<Caps, "ringDesignV67">): Chip[] {
  return caps.ringDesignV67 ? [...HELD_SETTINGS, ...JUNCTION_SETTINGS] : HELD_SETTINGS;
}

/** The pre-v50 flat list (served by the viewer): the v67 ids are dropped on a
 *  viewer that cannot build them, whatever it serves. */
export function servedSettingChips<T extends { id: string }>(served: readonly T[], caps: Pick<Caps, "ringDesignV67">): T[] {
  return caps.ringDesignV67 ? [...served] : served.filter((s) => !isJunctionSetting(s.id as DiamondSetting));
}

/**
 * Why a junction setting is dead for this style, or null. The engine gives a
 * cross-channel row and the two tension styles no beads or claws: the trench,
 * or the split band, is what holds those stones (`gemPlacement.markJunctions`).
 */
export function junctionBlockedReason(style: DiamondStyle): string | null {
  if (style === "cross-channel") return "A cross channel's trench holds these stones, so no beads or claws are added";
  if (style === "tension-1" || style === "tension-2") return "The split band holds a tension stone, so no beads or claws are added";
  return null;
}

/** The patch a Diamond Setting chip sends: the active chip toggles back to the
 *  plain seat (`setting: undefined`); any other sets it on the bare cut. */
export function settingChipPatch(id: DiamondSetting, on: boolean, bare: StoneShape): { setting?: DiamondSetting; stoneShape?: StoneShape } {
  return on ? { setting: undefined } : { setting: id, stoneShape: bare };
}

/** The bead/claw size slider exists only for the two junction settings, on a
 *  viewer that reads `junctionScale`, and never for a side run (which has its
 *  own side hardware and never reads the run's setting). */
export function showJunctionSize(caps: Pick<Caps, "ringDesignV67">, setting: DiamondSetting | undefined, side: boolean): boolean {
  return caps.ringDesignV67 && !side && isJunctionSetting(setting);
}

export const junctionSizeLabel = (setting: DiamondSetting | undefined) =>
  setting === "shared-prong" ? "Claw size" : "Bead size";

export const JUNCTION_SIZE = { min: JUNCTION_SCALE_MIN, max: JUNCTION_SCALE_MAX, step: 0.05 } as const;

/** The engine floor for a carved groove's width on THIS viewer: 0.15 mm from
 *  v67, 0.25 mm before (an older viewer clamps anything thinner back up, so a
 *  lower slider end would be dead track that snaps back under the finger). */
export function grooveWidthMin(caps: Pick<Caps, "ringDesignV67">): number {
  return caps.ringDesignV67 ? DESIGN_GROOVE_MIN_WIDTH_MM : 0.25;
}

/**
 * The Joint / Groove Width sliders step 0.25 on the reference's 12-tick ruler.
 * A range input steps from its MIN, so a 0.15 floor with a 0.25 step would make
 * 0.25, 0.50 … unreachable; from v67 the step is 0.05 and the ruler keeps its
 * marks at 0.25 … 3.00.
 */
export function quarterGrooveSlider(caps: Pick<Caps, "ringDesignV67">): { min: number; step: number; ticks: number | number[] } {
  return caps.ringDesignV67
    ? { min: grooveWidthMin(caps), step: 0.05, ticks: Array.from({ length: 12 }, (_, i) => 0.25 * (i + 1)) }
    : { min: 0.25, step: 0.25, ticks: 12 };
}

// ─── free stones ─────────────────────────────────────────────────────────────

/**
 * The carat a free stone is drawn at, by the engine's own precedence
 * (`gemPlacement.freeRun`): `sizeScale` (a multiple of the run's diameter, what
 * a pattern stamp writes) WINS over `sizeCt`, which wins over the run's size.
 * Carat goes with the cube of the diameter.
 */
export function freeStoneCarat(stone: Pick<FreeStone, "sizeCt" | "sizeScale">, runCarat: number): number {
  if (stone.sizeScale != null) return runCarat * stone.sizeScale ** 3;
  return stone.sizeCt ?? runCarat;
}

/**
 * The patch the Size field sends. It used to write `sizeCt` alone — and every
 * stamped stone carries `sizeScale`, which wins, so typing a size into a
 * stamped stone changed nothing. The patch now clears `sizeScale` (an
 * undefined key survives postMessage, and `updateFreeStone` spreads it over the
 * stone) so the carat typed is the one drawn: the stone becomes hand-sized,
 * like any stone added by clicking, and the engine still clamps that carat to
 * what the band's section can seat (`clampFreeStoneSize` reads `sizeCt`).
 */
export function freeStoneSizePatch(ct: number): Pick<FreeStone, "sizeCt" | "sizeScale"> {
  return { sizeCt: ct, sizeScale: undefined };
}

/** The per-stone Setting chips (v67): follow the row, or one of the held
 *  settings. Absent on an older viewer, which has no junction hardware. */
export function freeStoneSettingChips(caps: Pick<Caps, "ringDesignV67">): Chip[] {
  return caps.ringDesignV67 ? [...HELD_SETTINGS, ...JUNCTION_SETTINGS] : [];
}
